import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { ChatMessage, ChatNote, MessageClickHandler } from './chat-message.interface'
import { ChatMessageAvatar } from './chat-message-avatar.atom'
import { ChatMessageBody } from './chat-message-body.atom'
import {
  DEFAULT_AGENT_NAME,
  getBubbleVariantForMessage,
  getMessageSender,
  getMessageTypeClass,
  getSenderName,
  isMessageRightAligned,
} from './chat-message-item.helper'
import { ChatMessageSystem } from './chat-message-system.atom'

export interface ChatMessageItemProps {
  message: ChatMessage | ChatNote
  isOwn: boolean
  agentName?: string
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: MessageClickHandler
  onAttachmentDownload?: (attachmentId: string) => void
  className?: string
}

const renderSystemMessage = (
  message: ChatMessage | ChatNote,
  isPinned: boolean,
  onPin?: (itemId: string) => void,
  className?: string
): ReactNode | null => {
  if (message.type !== 'message') return null
  if (message.sender !== 'system') return null
  return (
    <ChatMessageSystem message={message} isPinned={isPinned} onPin={onPin} className={className} />
  )
}

const ChatMessageItemBase: React.FC<ChatMessageItemProps> = ({
  message,
  isOwn,
  agentName = DEFAULT_AGENT_NAME,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  onAttachmentDownload,
  className,
}): ReactNode => {
  const isNote = message.type === 'note'
  const sender = getMessageSender(message)
  const isAssistant = sender === 'assistant'
  const systemContent = renderSystemMessage(message, isPinned, onPin, className)
  if (systemContent) return systemContent
  const isRightAligned = isMessageRightAligned(isOwn, isNote, isAssistant)
  return (
    <div
      data-highlighted={isHighlighted ? 'true' : undefined}
      className={cn(
        'chat-message',
        getMessageTypeClass(isNote, isAssistant),
        'group flex gap-2 py-2',
        isRightAligned ? 'flex-row-reverse' : 'flex-row',
        className
      )}
    >
      <div className="flex-shrink-0 pt-1">
        <ChatMessageAvatar sender={sender} participant={message.participant} />
      </div>
      <ChatMessageBody
        message={message}
        senderName={getSenderName(isAssistant, agentName, message.participant?.name)}
        bubbleVariant={getBubbleVariantForMessage(message, sender)}
        isNote={isNote}
        isRightAligned={isRightAligned}
        isPinned={isPinned}
        isHighlighted={isHighlighted}
        onPin={onPin}
        onClick={onClick}
        onAttachmentDownload={onAttachmentDownload}
      />
    </div>
  )
}

export const ChatMessageItem = memo(ChatMessageItemBase)

ChatMessageItem.displayName = 'ChatMessageItem'
